import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Button } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { APP_PATHS } from '../../../../../../../../core/config/app-paths';
import { ConfirmDialogComponent } from '../../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {
  RODZAJ_PODMIOTU_LABELS,
  RodzajPodmiotu,
} from '../../../../../../../../shared/enums/rodzaj-podmiotu.enum';
import { AdminPanelUserDataSectionField } from '../../../../../../../../shared/models/admin-panel-user-data-section-field';
import { AdminPanelUserDetails } from '../../../../../../../../shared/models/admin-panel-user-details';
import { AdminPanelUserOrganizationDetails } from '../../../../../../../../shared/models/admin-panel-user-organization-details';
import { AdminPanelUsersNavigationContextService } from '../../../../../../../../shared/services/admin-panel-users-navigation-context.service';
import { AdminPanelUsersApi } from '../../../../../../../../shared/services/api/admin-panel-users.api';
import { OrganizacjaApi } from '../../../../../../../../shared/services/api/organizacja.api';
import { HttpResponseService } from '../../../../../../../../core/services/http-error-response.service';
import { SpinnerService } from '../../../../../../../../core/services/spinner.service';
import { AdminPanelUserOrganizationAddressAndBankAccountSectionComponent } from '../admin-panel-user-organization-address-and-bank-account-section/admin-panel-user-organization-address-and-bank-account-section.component';
import { AdminPanelUserOrganizationIdentificationSectionComponent } from '../admin-panel-user-organization-identification-section/admin-panel-user-organization-identification-section.component';
import { AdminPanelUserOrganizationOtherInformationSectionComponent } from '../admin-panel-user-organization-other-information-section/admin-panel-user-organization-other-information-section.component';
import { AdminPanelUserOrganizationRelatedPersonsSectionComponent } from '../admin-panel-user-organization-related-persons-section/admin-panel-user-organization-related-persons-section.component';

enum OrganizationStatusMessage {
  Pending = 'Dane organizacji zostały poprawnie wczytane z systemu Rejestr Żłobków. W systemie Rejestr Żłobków istnieją wiadomości oczekujące na wczytanie.',
  Completed = 'Dane organizacji zostały poprawnie wczytane z systemu Rejestr Żłobków. W systemie Rejestr Żłobków nie istnieją wiadomości oczekujące na wczytanie.',
}

@Component({
  selector: 'app-admin-panel-user-organization-preview',
  imports: [
    Button,
    AdminPanelUserOrganizationAddressAndBankAccountSectionComponent,
    AdminPanelUserOrganizationIdentificationSectionComponent,
    AdminPanelUserOrganizationOtherInformationSectionComponent,
    AdminPanelUserOrganizationRelatedPersonsSectionComponent,
  ],
  templateUrl: './admin-panel-user-organization-preview.html',
  styleUrl: './admin-panel-user-organization-preview.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelUserOrganizationPreview {
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialogService = inject(DialogService);
  readonly #adminPanelUsersApi = inject(AdminPanelUsersApi);
  readonly #organizacjaApi = inject(OrganizacjaApi);
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #router = inject(Router);
  readonly #navigationContext = inject(AdminPanelUsersNavigationContextService);

  protected readonly userDetails = computed<AdminPanelUserDetails | null>(() =>
    this.#navigationContext.userDetails(),
  );
  protected readonly organizationId = computed(
    () => this.#navigationContext.organizationId()!,
  );
  protected readonly organization =
    signal<AdminPanelUserOrganizationDetails | null>(null);

  constructor() {
    effect(() => {
      if (!this.#navigationContext.hasUserOrganizationDetailsContext()) {
        return;
      }

      const userDetails = this.userDetails();
      if (!userDetails) {
        return;
      }

      this.#loadOrganizationDetails(userDetails, this.organizationId());
    });
  }

  protected readonly identificationSectionFields = computed<
    AdminPanelUserDataSectionField[]
  >(() => {
    const organization = this.organization();

    if (!organization) {
      return [];
    }

    return [
      {
        label: 'Nazwa organizacji',
        value: organization.nazwa,
      },
      {
        label: 'NIP',
        value: organization.nip,
      },
      {
        label: 'REGON',
        value: organization.regon,
      },
      {
        label: 'Adres e-mail',
        value: organization.email,
      },
      {
        label: 'Telefon komórkowy',
        value: organization.telefon1,
      },
      {
        label: 'WWW',
        value: organization.www,
      },
      {
        label: 'PESEL',
        value: organization.pesel,
      },
      {
        label: 'Numer lub indeks w rejestrze publicznym',
        value: organization.indeks,
      },
      {
        label: 'Nazwa rejestru',
        value: organization.indeksNazwa,
      },
      {
        label: 'Rodzaj podmiotu',
        value: this.#getOrganizationTypeLabel(organization.rodzajPodmiotu),
      },
    ];
  });

  protected readonly addressAndBankAccountSectionFields = computed<
    AdminPanelUserDataSectionField[]
  >(() => {
    const organization = this.organization();

    if (!organization) {
      return [];
    }

    return [
      {
        label: 'Adres siedziby podmiotu',
        value: organization.adresSiedziby,
      },
      {
        label:
          'Numer rachunku bankowego podmiotu prowadzącego żłobek, klub dziecięcy, zatrudnionego opiekuna dziennego, opiekuna prowadzącego działalność na własny rachunek',
        value: organization.numerRachunku,
        itemClass: 'col-span-2',
      },
    ];
  });

  protected readonly otherInformationSectionFields = computed<
    AdminPanelUserDataSectionField[]
  >(() => {
    const organization = this.organization();

    if (!organization) {
      return [];
    }

    return [
      {
        label: 'Data ostatniej aktualizacji',
        value: organization.dataOstatniejAktualizacji,
        itemClass: 'md:col-span-3',
      },
      {
        label: 'Informacja o statusie wniosków',
        value: this.#getOrganizationStatusInformationLabel(
          organization.czyWiadomosciOczekujaceNaWczytanie,
          organization.dataOstatniejAktualizacji,
        ),
        itemClass: 'md:col-span-3',
      },
    ];
  });

  protected goBack(): void {
    const userDetails = this.userDetails();

    if (!userDetails) {
      return;
    }

    this.#navigationContext.setUserDetailsContext(userDetails);
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.users}/${APP_PATHS.admin.details}`,
    ]);
  }

  protected downloadOrganizationData(): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: 'Potwierdzenie pobrania danych organizacji',
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      width: '700px',
      breakpoints: {
        '960px': '90vw',
        '640px': '95vw',
      },
      inputValues: {
        description:
          'Czy potwierdzasz zamiar wysłania żądania aktualizacji danych organizacji?',
        confirmButtonLabel: 'Tak',
      },
    });

    if (!ref) {
      return;
    }

    ref.onClose
      .pipe(
        take(1),
        filter(Boolean),
        tap(() => this.#spinnerService.show()),
        switchMap(() => this.#refreshOrganizationData$()),
        tap(({ organizationDetails, refreshResult }) => {
          this.organization.set({
            ...organizationDetails,
            ...refreshResult,
          });

          this.#httpResponseService.showSuccessMessage(
            'Pobrano dane organizacji z Rejestru Żłobków.',
          );
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobierania danych organizacji.',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #getOrganizationStatusInformationLabel(
    value: boolean | null | undefined,
    dataOstatniejAktualizacjiValue: string | null,
  ): string | null {
    if (value == null || dataOstatniejAktualizacjiValue === null) {
      return null;
    }

    return value
      ? OrganizationStatusMessage.Pending
      : OrganizationStatusMessage.Completed;
  }

  #getOrganizationTypeLabel(
    value: RodzajPodmiotu | null | undefined,
  ): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    return RODZAJ_PODMIOTU_LABELS[value] ?? null;
  }

  #loadOrganizationDetails(
    userDetails: AdminPanelUserDetails,
    organizationId: number,
  ): void {
    this.#organizacjaApi
      .getAdminOrganizationDetails$(userDetails, organizationId)
      .pipe(
        tap((organizationDetails) => {
          this.organization.set(organizationDetails);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #refreshOrganizationData$(): Observable<any> {
    const userDetails = this.userDetails();

    if (!userDetails) {
      return EMPTY;
    }

    return this.#adminPanelUsersApi
      .refreshOrganizationData$(this.organizationId())
      .pipe(
        switchMap((refreshResult) =>
          this.#organizacjaApi
            .getAdminOrganizationDetails$(userDetails, this.organizationId())
            .pipe(
              map((organizationDetails) => ({
                organizationDetails,
                refreshResult,
              })),
            ),
        ),
      );
  }
}
